/**
 * Shopify Cart and Recommendations System
 * Handles cart functionality, product recommendations, and UI updates
 */

/**
 * Initialize Shopify routes if not already set
 * Ensures basic route functionality is available
 */
if (!window.Shopify || !window.Shopify.routes || !window.Shopify.routes.root) {
  console.error('Shopify routes not properly initialized');
  window.Shopify = window.Shopify || {};
  window.Shopify.routes = window.Shopify.routes || {};
  window.Shopify.routes.root = '/';
}

/** Global reference for the recommendations slider */
let cartRecommendationsSwiper;

/**
* Formats price from cents to euros with German locale
* @param {number} cents - Price in cents
* @returns {string} Formatted price string (e.g., "19,99€")
*/
function formatPriceWithCents(cents) {
  const euros = (cents / 100).toFixed(2);
  return euros.replace('.', ',') + '€';
}

/**
* Handles add to cart events with delay for recommendations
* @fires checkCartAndFetchRecommendations
*/
function handleAddToCart() {
  setTimeout(() => {
      checkCartAndFetchRecommendations();
  }, 1000);
}

// Event listeners for cart additions
document.addEventListener('click', (event) => {
  const addToCartButton = event.target.closest('[data-add-to-cart]');
  if (addToCartButton) {
      handleAddToCart();
  }
});

document.addEventListener('submit', (event) => {
  if (event.target.querySelector('[data-add-to-cart]')) {
      handleAddToCart();
  }
});

/**
* Debounced function to fetch recommendations
* Prevents multiple rapid requests
*/
const debouncedFetchRecommendations = debounce(() => {
  const cartDrawer = document.querySelector('cart-drawer');
  const recommendationsContainer = document.getElementById('CartDrawer-Recommendations');

  if (cartDrawer && !cartDrawer.classList.contains('is-empty') && !cartDrawer.classList.contains('is-transitioning')) {
      fetchRecommendedProducts();
  } else {
      setTimeout(() => {
          debouncedFetchRecommendations();
      }, 500);
  }
}, 300);

/**
* Triggers recommendation fetch with debounce
*/
function checkCartAndFetchRecommendations() {
  debouncedFetchRecommendations();
}

/**
* Fetches current cart items from Shopify
* @returns {Promise<Object>} Cart data including items, total price, and count
*/
async function fetchCartItems() {
  try {
      const response = await fetch('/cart.js');
      if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
      }
      const cart = await response.json();
      return {
          items: cart.items || [],
          total_price: cart.total_price,
          item_count: cart.item_count
      };
  } catch (error) {
      console.error('Error fetching cart items:', error);
      return { items: [], total_price: 0, item_count: 0 };
  }
}

/**
* Removes duplicate recommendations and filters out cart items
* @param {Array} recommendations - Array of recommended products
* @param {Set} cartProductIds - Set of product IDs currently in cart
* @returns {Array} Unique recommendations not in cart
*/
function deduplicateRecommendations(recommendations, cartProductIds) {
  const uniqueRecommendations = new Map();
  recommendations.forEach(product => {
      const productId = product.id.toString();
      if (!cartProductIds.has(productId) && !uniqueRecommendations.has(productId)) {
          uniqueRecommendations.set(productId, product);
      }
  });
  return Array.from(uniqueRecommendations.values());
}

/** Tracks last fetched recommendations for comparison */
let lastFetchedRecommendations = [];

/**
* Fetches and displays recommended products
* Handles recommendation fetching, deduplication, and UI updates
* @async
*/
async function fetchRecommendedProducts() {
  const recommendationsContainer = document.getElementById('CartDrawer-Recommendations');
  const recommendationsWrapper = recommendationsContainer?.querySelector('.mu-cart-recommendations-slider-wrapper');

  if (!recommendationsContainer || !recommendationsWrapper) {
      console.error('Required elements not found');
      return;
  }

  // Hide container during update
  recommendationsContainer.style.display = 'none';
  recommendationsContainer.style.opacity = '0';

  try {
      const cart = await fetchCartItems();
      if (cart.item_count === 0) return;

      const cartProductIds = new Set(cart.items.map(item => item.product_id.toString()));
      let allRecommendations = [];

      // Fetch recommendations for each cart item
      for (const item of cart.items) {
          if (!item.product_id) continue;

          try {
              const response = await fetch(
                  `${window.Shopify.routes.root}recommendations/products.json?product_id=${item.product_id}&limit=4&intent=related`
              );
              if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
              const data = await response.json();
              if (data?.products) {
                  allRecommendations = allRecommendations.concat(data.products);
              }
          } catch (error) {
              console.error('Error fetching recommendations for product:', item.product_id, error);
          }
      }

      // Remove duplicates and current cart items
      allRecommendations = allRecommendations.filter((product, index, self) =>
          index === self.findIndex((t) => t.id === product.id)
      );

      const uniqueRecommendations = allRecommendations.filter(
          product => !cartProductIds.has(product.id.toString())
      );

      if (uniqueRecommendations.length === 0) return;

      const newRecommendations = uniqueRecommendations.slice(0, 8);
      recommendationsWrapper.innerHTML = '';

      // Create and append recommendation slides
      newRecommendations.forEach(product => {
          const slideDiv = document.createElement('div');
          slideDiv.className = 'mu-cart-recommendations-slider-slide tw-flex tw-flex-col tw-items-center tw-min-w-[200px] md:tw-min-w-[200px] tw-max-w-[200px] md:tw-max-w-[200px]';
          const formattedPrice = formatPriceWithCents(product.price);

          slideDiv.innerHTML = `
              <a href="${product.url}">
                  <div class="tw-text-left tw-flex tw-flex-col">
                      <div class="tw-flex tw-flex-row tw-w-full tw-justify-center image-wrapper tw-mb-xs">
                          <img src="${product.featured_image}" alt="${product.title}" class="tw-w-[200px] tw-h-[200px] md:tw-w-[200px] md:tw-h-[200px] tw-object-cover tw-aspect-square" width="200" height="200">
                      </div>
                      <div class="tw-flex tw-flex-col">
                          <span class="tw-text-textLg tw-transition-colors tw-duration-300 hover:tw-text-linkFgHover">${product.title}</span>
                          <span class="tw-block tw-text-textLg tw-transition-colors tw-duration-300">${formattedPrice}</span>
                      </div>
                  </div>
              </a>
          `;
          recommendationsWrapper.appendChild(slideDiv);
      });

      // Show and initialize recommendations
      recommendationsContainer.style.display = 'flex';
      requestAnimationFrame(() => {
          initializeCartRecommendationsSlider();
          setTimeout(() => {
              recommendationsContainer.style.opacity = '1';
          }, 50);
      });

  } catch (error) {
      console.error('Error fetching recommendations:', error);
  }
}

/**
 * Initializes the recommendations slider using Swiper
 * Sets up slider configuration and navigation
 */
function initializeCartRecommendationsSlider() {
  const slider = document.querySelector('.mu-cart-recommendations-slider-container');
  if (slider && typeof Swiper !== 'undefined') {
      // Destroy existing swiper instance if it exists
      if (cartRecommendationsSwiper) {
          cartRecommendationsSwiper.destroy();
      }

      // Initialize new Swiper instance with configuration
      cartRecommendationsSwiper = new Swiper(slider, {
          wrapperClass: 'mu-cart-recommendations-slider-wrapper',
          slideClass: 'mu-cart-recommendations-slider-slide',
          slidesPerView: 'auto',
          spaceBetween: 20,
          freeMode: true,
          loop: false,
          observer: true,
          observeParents: true,
          watchOverflow: true,
          navigation: {
              nextEl: '.mu-cart-recommendations-slider-button-next',
              prevEl: '.mu-cart-recommendations-slider-button-prev',
          },
          on: {
              init: function(swiper) {
                  updateNavigationButtonsVisibility(swiper);
              },
              resize: function(swiper) {
                  updateNavigationButtonsVisibility(swiper);
              },
              slideChange: function(swiper) {
                  updateNavigationButtonsVisibility(swiper);
              },
              reachEnd: function(swiper) {
                  updateNavigationButtonsVisibility(swiper);
              }
          }
      });

      // Update swiper after initialization
      setTimeout(() => {
          cartRecommendationsSwiper.update();
          updateNavigationButtonsVisibility(cartRecommendationsSwiper);
      }, 100);
  }
}

/**
* Updates the visibility of navigation buttons based on slider position
* @param {Object} swiper - Swiper instance
*/
function updateNavigationButtonsVisibility(swiper) {
  const prevButton = swiper.el.querySelector('.mu-cart-recommendations-slider-button-prev');
  const nextButton = swiper.el.querySelector('.mu-cart-recommendations-slider-button-next');

  if (prevButton) {
      prevButton.style.display = swiper.isBeginning ? 'none' : 'flex';
  }

  if (nextButton) {
      nextButton.style.display = swiper.isEnd ? 'none' : 'flex';
  }
}

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));

    // -- Muun: Get total price and inject the shipping progress bar ------
    const totalPrice = document.querySelector(".totals__total-value");
    if (totalPrice) {
      this.loadAndInjectShippingProgressBar(this.convertCurrencyToCents(totalPrice.innerHTML));
    }

    // Add MutationObserver to detect cart drawer opening
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            if (!cartDrawer.classList.contains('is-empty') && !cartDrawer.classList.contains('is-transitioning')) {
              setTimeout(() => {
                checkCartAndFetchRecommendations();
              }, 500); // Add a slight delay to ensure the cart is fully loaded
            }
          }
        });
      });

      observer.observe(cartDrawer, { attributes: true });
    }

    // Add event listener for cart updates
    document.addEventListener('cart:updated', (event) => {
      setTimeout(() => {
        checkCartAndFetchRecommendations();
      }, 500); // Add a slight delay to ensure the cart is fully updatedc
      
    });

    // Add event listener for cart drawer opening
    document.addEventListener('cart-drawer:opened', (event) => {
      setTimeout(() => {
        this.initializeCartRecommendations();
      }, 500);
    });

    // Add event listener for quantity changes
    this.addEventListener('change', (event) => {
      if (event.target.matches('[data-quantity-variant-id]')) {
        setTimeout(() => {
          checkCartAndFetchRecommendations();
        }, 500);
      }
    });
  }

  cartUpdateUnsubscriber = undefined;checkVisibilityc

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  onChange(event) {
    this.updateQuantity(event.target.dataset.index, event.target.value, document.activeElement.getAttribute('name'), event.target.dataset.quantityVariantId);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
        for (const selector of selectors) {
          const targetElement = document.querySelector(selector);
          const sourceElement = html.querySelector(selector);
          if (targetElement && sourceElement) {
            targetElement.replaceWith(sourceElement);
          }
        }

        // -- Muun: Get total price and inject the shipping progress bar ------
        const totalPrice = document.querySelector(".totals__total-value");
        if (totalPrice) {
          this.loadAndInjectShippingProgressBar(this.convertCurrencyToCents(totalPrice.innerHTML));
        }

        // Trigger recommendations fetch after cart update
        window.checkCartAndFetchRecommendations();
      })
      .catch((e) => {
        console.error(e);
      });
    } else {
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });
        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });

        // --- Muun: Inject the shipping progress bar ------
        this.loadAndInjectShippingProgressBar(parsedState.total_price);
  
        // Trigger recommendations fetch after quantity update
        setTimeout(() => {
          checkCartAndFetchRecommendations();
        }, 500); // Add a slight delay to ensure the cart is fully updated

        // Dispatch a custom event to notify that the cart has been updated
        document.dispatchEvent(new CustomEvent('cart:updated'));
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').innerHTML = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }

  // --- Muun: Inject the shipping progress bar ------
  async loadAndInjectShippingProgressBar(totalPrice) {
    const shippingProgressBar = await this.getShippingProgressBar(totalPrice);
    const cartDrawerInner = document.querySelector('.drawer__inner');

    if (cartDrawerInner && shippingProgressBar) {
      // Remove the existing progress bar
      const existingProgressBar = cartDrawerInner.querySelector('.drawer__progress-bar');
      if (existingProgressBar) {
        existingProgressBar.parentNode.removeChild(existingProgressBar);
      }

      // Insert the new progress bar
      const drawerHeader = cartDrawerInner.querySelector('.drawer__header');
      drawerHeader.parentNode.insertBefore(shippingProgressBar, drawerHeader.nextSibling);
    }
  }

  // --- Muun: Create the shipping progress bar ------
  getShippingProgressBar(totalPrice) {
    const shippingProgressBar = document.createElement('div');
    shippingProgressBar.classList.add('drawer__progress-bar');
    shippingProgressBar.style.padding = 'var(--mu-spacing-3xl) var(--mu-spacing-xl)';
    shippingProgressBar.style.fontSize = 'var(--mu-font-size-text-md)';

    let freeShippingThreshold = 5000;

    if (Shopify.country == "CH") {
      freeShippingThreshold = 9900
    }

    const remainingAmount = freeShippingThreshold - totalPrice;
    const percentageCompleted = (totalPrice * 100) / freeShippingThreshold;

    const progressBarContainer = document.createElement('div');
    progressBarContainer.style.display = 'flex';
    progressBarContainer.style.flexDirection = 'column';

    if (remainingAmount > 0) {
      progressBarContainer.style.gap = 'var(--mu-spacing-sm)';

      const span = document.createElement('span');

      //TODO: Checken ob es durch Schema ersetzt werden kann
      if (Shopify.country == "CH") {
        span.textContent = `Versandkostenfrei (in CH) ab ${this.formatSwissCurrency(freeShippingThreshold)}`;
      } else {
        span.textContent = `Versandkostenfrei (in DE) ab ${this.formatCurrency(freeShippingThreshold)}`;
      }

      progressBarContainer.appendChild(span);

      const fullProgressBar = document.createElement('div');
      fullProgressBar.style.width = '100%';
      fullProgressBar.style.height = '2px';
      fullProgressBar.style.backgroundColor = 'var(--colors-gray-200)';

      const progressStatus = document.createElement('div');
      progressStatus.style.height = '100%';
      progressStatus.style.backgroundColor = 'var(--mu-button-primary-bg)';
      progressStatus.style.position = 'relative';
      progressStatus.style.width = percentageCompleted + '%';

      const heartIconWrapper = document.createElement('div');
      heartIconWrapper.style.position = 'absolute';
      heartIconWrapper.style.top = '50%';
      heartIconWrapper.style.right = '0';
      heartIconWrapper.style.transform = 'translate(50%, -50%)';
      heartIconWrapper.style.color = 'var(--mu-button-primary-bg)';
      heartIconWrapper.style.backgroundColor = 'var(--mu-bg-primary-white)';
      heartIconWrapper.style.padding = '2px';

      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('width', '16');
      svg.setAttribute('height', '14');
      svg.setAttribute('viewBox', '0 0 24 23');
      svg.setAttribute('fill', 'none');

      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', 'M12.0004 5.92678C11.3821 1.16458 4.68117 -2.80669 1.12736 2.60038C-3.86889 10.2018 9.21596 20.2922 11.6651 21.9803C11.8668 22.1198 12.1332 22.1198 12.3349 21.9803C14.784 20.2922 27.8689 10.2018 22.8726 2.60038C19.3197 -2.80586 12.6179 1.16541 12.0004 5.92761V5.92678Z');
      path.setAttribute('fill', '#141414');

      svg.appendChild(path);
      heartIconWrapper.appendChild(svg);
      progressStatus.appendChild(heartIconWrapper);
      fullProgressBar.appendChild(progressStatus);
      progressBarContainer.appendChild(fullProgressBar);

      const lightText = document.createElement('p');
      lightText.style.color = 'var(--colors-gray-500)';

      if (Shopify.country == "CH") {
        lightText.innerHTML = `Nur noch <span class="remaining-amount" style="color: var(--colors-gray-900)">${this.formatSwissCurrency(remainingAmount)}</span>, dann ist der Versand für dich komplett kostenlos.`;
      } else {
        lightText.innerHTML = `Nur noch <span class="remaining-amount" style="color: var(--colors-gray-900)">${this.formatCurrency(remainingAmount)}</span>, dann ist der Versand für dich komplett kostenlos.`;
      }

      progressBarContainer.appendChild(lightText);

      shippingProgressBar.appendChild(progressBarContainer);
    } else {
      progressBarContainer.style.gap = '0px';

      const span = document.createElement('span');
      span.textContent = 'Versandkosten';
      progressBarContainer.appendChild(span);

      const lightText = document.createElement('span');
      lightText.style.color = 'var(--colors-gray-500)';
      lightText.textContent = 'Glückwunsch, deine Bestellung ist versandkostenfrei 🖤';
      progressBarContainer.appendChild(lightText);

      shippingProgressBar.appendChild(progressBarContainer);
    }

    return shippingProgressBar;
  }

  // --- Muun: Convert currency with currency symbols etc. ------
  // TODO: Checken ob dies mit anderen Währungen funktioniert, nicht nur Euros
  convertCurrencyToCents(currencyString) {
    const amount = parseFloat(currencyString.replace(/[^\d.,]/g, '').replace(',', '.'));
    const cents = Math.round(amount * 100);

    return cents;
  }

  formatCurrency(currencyValue) {
    return (currencyValue / 100).toLocaleString('de-DE', {
      style: 'currency',
      currency: 'EUR'
    });
  }

  // --- Muun: Format currency to Swiss Franc ------
  formatSwissCurrency(currencyValue) {
    return (currencyValue / 100).toLocaleString('de-CH', {
      style: 'currency',
      currency: 'CHF'
    });
  }

  initializeCartRecommendations() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && !cartDrawer.classList.contains('is-empty') && !cartDrawer.classList.contains('is-transitioning')) {
      checkCartAndFetchRecommendations();
    }
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}

// Make checkCartAndFetchRecommendations available globally
window.checkCartAndFetchRecommendations = checkCartAndFetchRecommendations;

// Add event listener for add to cart button clicks
document.addEventListener('click', (event) => {
  const addToCartButton = event.target.closest('[data-add-to-cart]');
  if (addToCartButton) {
    handleAddToCart();
  }
});

// Add a new event listener for form submissions (in case the add to cart is a form submit)
document.addEventListener('submit', (event) => {
  if (event.target.querySelector('[data-add-to-cart]')) {
    handleAddToCart();
  }
});

// Add this new function
function initializeCartRecommendations() {
  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer && !cartDrawer.classList.contains('is-empty') && !cartDrawer.classList.contains('is-transitioning')) {
    checkCartAndFetchRecommendations();
  }
}

function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

document.addEventListener('DOMContentLoaded', function() {
  if (typeof Swiper === 'undefined') {
    console.error('Swiper library not loaded');
  }
});